<template>
  <div v-click-outside="handleClickOutside" class="border-top p-2 d-flex align-items-end rs-bg-content">
    <!-- Column Manager -->
    <div class="dropdown mr-2">
      <button
        id="tiptap-table-columns-toggle"
        type="button"
        class="btn btn-light btn-sm dropdown-toggle"
        aria-haspopup="true"
        :aria-expanded="openMenu === 'columns'"
        @click="toggleMenu('columns', $event)"
      >
        Columns
      </button>
      <Teleport to="body">
        <div
          v-if="openMenu === 'columns'"
          ref="menu"
          class="dropdown-menu show"
          aria-labelledby="tiptap-table-columns-toggle"
          :style="menuStyle"
        >
          <button
            type="button"
            class="dropdown-item"
            @click="editor.chain().focus(position).addColumnBefore().run(); closeMenu()"
          >
            <i class="far fa-plus mr-2" />Add Column Left
          </button>
          <button
            type="button"
            class="dropdown-item"
            @click="editor.chain().focus(position).addColumnAfter().run(); closeMenu()"
          >
            <i class="far fa-plus mr-2" />Add Column Right
          </button>

          <div class="dropdown-divider" />

          <button
            type="button"
            class="dropdown-item"
            @click="editor.chain().focus(position).toggleHeaderColumn().run(); closeMenu()"
          >
            <i class="fad fa-grip-lines-vertical mr-2" />Toggle Column Headers
          </button>

          <div class="dropdown-divider" />

          <button
            type="button"
            class="dropdown-item"
            @click="editor.chain().focus(position).deleteColumn().run(); closeMenu()"
          >
            <i class="far fa-trash-alt mr-2 rs-text-error" />Remove Column
          </button>
        </div>
      </Teleport>
    </div>

    <!-- Row Manager -->
    <div class="dropdown mr-2">
      <button
        id="tiptap-table-rows-toggle"
        type="button"
        class="btn btn-light btn-sm dropdown-toggle"
        aria-haspopup="true"
        :aria-expanded="openMenu === 'rows'"
        @click="toggleMenu('rows', $event)"
      >
        Rows
      </button>
      <Teleport to="body">
        <div
          v-if="openMenu === 'rows'"
          ref="menu"
          class="dropdown-menu show"
          aria-labelledby="tiptap-table-rows-toggle"
          :style="menuStyle"
        >
          <button
            type="button"
            class="dropdown-item"
            @click="editor.chain().focus(position).addRowBefore().run(); closeMenu()"
          >
            <i class="far fa-plus mr-2" />Add Row Above
          </button>
          <button
            type="button"
            class="dropdown-item"
            @click="editor.chain().focus(position).addRowAfter().run(); closeMenu()"
          >
            <i class="far fa-plus mr-2" />Add Row Below
          </button>

          <div class="dropdown-divider" />

          <button
            type="button"
            class="dropdown-item"
            @click="editor.chain().focus(position).toggleHeaderRow().run(); closeMenu()"
          >
            <i class="fad fa-grip-lines mr-2" />Toggle Row Headers
          </button>

          <div class="dropdown-divider" />

          <button
            type="button"
            class="dropdown-item"
            @click="editor.chain().focus(position).deleteRow().run(); closeMenu()"
          >
            <i class="far fa-trash-alt mr-2 rs-text-error" />Remove Row
          </button>
        </div>
      </Teleport>
    </div>

    <!-- Table Proeprties Manager -->
    <div class="dropdown">
      <button
        id="tiptap-table-props-toggle"
        type="button"
        class="btn btn-light btn-sm dropdown-toggle"
        aria-haspopup="true"
        :aria-expanded="openMenu === 'table'"
        @click="toggleMenu('table', $event)"
      >
        Table
      </button>
      <Teleport to="body">
        <div
          v-if="openMenu === 'table'"
          ref="menu"
          class="dropdown-menu show"
          aria-labelledby="tiptap-table-props-toggle"
          :style="menuStyle"
        >
          <form class="px-4 py-3" :style="{ width: '300px' }">
            <div class="input-group input-group-sm mb-3">
              <div class="input-group-prepend">
                <span class="input-group-text">Width</span>
              </div>
              <input
                v-model.number="width"
                type="number"
                class="form-control"
                @input="handleWidthChange(($event.target as HTMLInputElement).value)"
              />

              <div class="input-group-append">
                <button
                  type="button"
                  class="btn"
                  :class="tableWidthUoM === 'px' ? 'btn-secondary' : 'btn-outline-secondary'"
                  title="Table width in pixels"
                  @click="handleWidthUoMChange('px')"
                >
                  px
                </button>
                <button
                  type="button"
                  class="btn"
                  :class="tableWidthUoM === '%' ? 'btn-secondary' : 'btn-outline-secondary'"
                  title="Table width in percentage"
                  @click="handleWidthUoMChange('%')"
                >
                  &percnt;
                </button>
              </div>
            </div>

            <div class="input-group input-group-sm mb-3">
              <div class="input-group-prepend">
                <span class="input-group-text">Border</span>
              </div>
              <input
                v-model.number="border"
                type="number"
                class="form-control"
                @input="handleBorderChange(($event.target as HTMLInputElement).value)"
              />
              <div class="input-group-append">
                <span class="input-group-text">px</span>
              </div>
            </div>

            <div class="input-group input-group-sm mb-3">
              <div class="input-group-prepend">
                <span class="input-group-text">Cell Padding</span>
              </div>
              <input
                v-model.number="cellPadding"
                type="number"
                class="form-control"
                @input="handleCellPaddingChange(($event.target as HTMLInputElement).value)"
              />
              <div class="input-group-append">
                <span class="input-group-text">px</span>
              </div>
            </div>

            <div class="input-group input-group-sm mb-3">
              <div class="input-group-prepend">
                <span class="input-group-text">Cell Spacing</span>
              </div>
              <input
                v-model.number="cellSpacing"
                type="number"
                class="form-control"
                @input="handleCellSpacingChange(($event.target as HTMLInputElement).value)"
              />
              <div class="input-group-append">
                <span class="input-group-text">px</span>
              </div>
            </div>

            <div class="input-group input-group-sm">
              <div class="input-group-prepend">
                <span class="input-group-text">Align</span>
              </div>
              <select v-model="align" class="custom-select" @change="handleAlignChange(($event.target as HTMLSelectElement).value)">
                <option v-for="option in tblAlignOptions" :key="option.value" :value="option.value">
                  {{ option.text }}
                </option>
              </select>
            </div>
          </form>

          <div class="dropdown-divider" />

          <button type="button" class="dropdown-item" @click="handleDelete(); closeMenu()">
            <i class="far fa-trash-alt mr-2 rs-text-error" />Remove Table
          </button>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { Editor } from '@tiptap/vue-3';
import { tblAlignOptions } from '@/shared/tiptap/consts';
import { findNodePosByElement, setNodePosAttributes } from '@/shared/tiptap/helpers';
import { TEditableTable } from '@/shared/tiptap/types';

/** Which of the three toolbar dropdowns is currently open. */
type TTableMenu = 'columns' | 'rows' | 'table';

/** Keeps a pinned menu clear of the viewport edges. */
const MENU_VIEWPORT_GAP = 8;

/** Below this a capped menu is unusable, so let it overhang rather than collapse to nothing. */
const MENU_MIN_HEIGHT = 120;

export default defineComponent({
  props: {
    editor: {
      type: Object as PropType<Editor>,
      required: true,
    },

    onTableRemove: {
      type: Function as PropType<() => void>,
      default: () => {},
    },
  },

  data() {
    return {
      tblAlignOptions,
      position: 0,
      tableWidthUoM: '%',
      border: 1,
      width: 100,
      cellSpacing: 1,
      cellPadding: 1,
      align: '',
      openMenu: '' as TTableMenu | '',
      menuAnchor: undefined as HTMLElement | undefined,
      menuStyle: {} as Record<string, string>,
      menuFrame: undefined as number | undefined,
      menuAnchorRect: '',
    };
  },

  watch: {
    'editor.state.selection': {
      immediate: true,
      handler(newVal: { from: number, to: number }) {
        if (this.editor.isFocused && this.editor.isActive('table')) {
          this.position = newVal.from;
        }
      },
    },
  },

  mounted() {
    // Timeout workaround that ensures Vue picks up UI elements in the mounted hook.
    // The same workaround is often used for $refs.
    // Vue claims to have refs / ui available in mounted, but it's bulls**t
    setTimeout(() => {
      this.setInitialState();
    }, 100);
  },

  beforeUnmount() {
    this.stopTrackingAnchor();
  },

  methods: {
    /**
     * Opens `name`, or closes it if it is already open. Only one menu is open at a time.
     *
     * The anchor element is captured here rather than looked up by ref because the menu is
     * teleported out of this component's subtree, so it has no DOM relationship to its toggle.
     */
    toggleMenu(name: TTableMenu, event: MouseEvent) {
      const next = this.openMenu === name ? '' : name;
      this.openMenu = next;

      if (!next) {
        this.stopTrackingAnchor();
        return;
      }

      this.menuAnchor = event.currentTarget instanceof HTMLElement ? event.currentTarget : undefined;
      // The menu does not exist in the DOM until the v-if has rendered, so it cannot be measured yet.
      this.$nextTick(() => {
        this.positionMenu();
        this.trackAnchor();
      });
    },

    /**
     * Closes whichever menu is open. Called by every menu item and by `v-click-outside`.
     */
    closeMenu() {
      this.openMenu = '';
      this.menuAnchor = undefined;
      this.stopTrackingAnchor();
    },

    stopTrackingAnchor() {
      if (this.menuFrame !== undefined) {
        window.cancelAnimationFrame(this.menuFrame);
        this.menuFrame = undefined;
      }

      this.menuAnchorRect = '';
    },

    /**
     * Re-pins the open menu to its toggle for as long as it stays open, by watching the toggle's
     * rect each frame and repositioning only when it actually moves.
     *
     * A frame loop rather than scroll/resize listeners on purpose. Listeners are both too narrow and
     * too early: the app scrolls inside OverlayScrollbars containers (whose scroll events do not
     * reach `window`), and on `resize` the surrounding flex layout has not been recomputed when the
     * event fires -- even one frame later the anchor still reported its old rect, so the menu stayed
     * pinned where the button used to be until a *second* resize arrived. Polling the rect is
     * immune to whatever moved the button (scroll, resize, sidebar collapse, editor reflow).
     *
     * Cost is one `getBoundingClientRect` per frame, and only while a menu is open; the expensive
     * part (`positionMenu`, which forces a reflow to measure the menu) runs only on an actual change.
     */
    trackAnchor() {
      this.menuFrame = window.requestAnimationFrame(() => {
        this.menuFrame = undefined;

        if (!this.openMenu || !this.menuAnchor) {
          return;
        }

        const { top, left, bottom, width } = this.menuAnchor.getBoundingClientRect();
        const rect = `${top},${left},${bottom},${width},${window.innerWidth},${window.innerHeight}`;

        if (rect !== this.menuAnchorRect) {
          this.menuAnchorRect = rect;
          this.positionMenu();
        }

        this.trackAnchor();
      });
    },

    /**
     * `v-click-outside` fires for clicks anywhere outside this toolbar, which now includes the
     * teleported menu itself -- so ignore clicks that landed inside the open menu.
     */
    handleClickOutside(event: MouseEvent) {
      const { menu } = this.$refs;

      if (menu instanceof HTMLElement && event.target instanceof Node && menu.contains(event.target)) {
        return;
      }

      this.closeMenu();
    },

    /**
     * Pins the teleported menu to its toggle button using fixed coordinates.
     *
     * The menu has to leave this component's subtree: `.rs-form-tiptap` sets `overflow: hidden`, and
     * an absolutely-positioned menu is clipped by it -- the Table properties form is ~318px tall and
     * only ~118px of the `basic` editor sits below the toolbar. Popper handled the flip (though not
     * the clipping) for the BootstrapVue dropdown this replaced; this reproduces it: prefer below the
     * toggle, flip above when the menu would run off the viewport, and cap the height with internal
     * scrolling when it fits neither way.
     */
    positionMenu() {
      const anchor = this.menuAnchor;
      const { menu } = this.$refs;

      if (!anchor || !(menu instanceof HTMLElement)) {
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - MENU_VIEWPORT_GAP;
      const spaceAbove = rect.top - MENU_VIEWPORT_GAP;
      // measure the menu unconstrained so a previous cap does not feed back into this calculation
      menu.style.maxHeight = '';
      const height = menu.offsetHeight;
      const flip = height > spaceBelow && spaceAbove > spaceBelow;
      const available = flip ? spaceAbove : spaceBelow;

      this.menuStyle = {
        position: 'fixed',
        top: `${flip ? Math.max(MENU_VIEWPORT_GAP, rect.top - Math.min(height, available)) : rect.bottom}px`,
        left: `${Math.max(MENU_VIEWPORT_GAP, Math.min(rect.left, window.innerWidth - menu.offsetWidth - MENU_VIEWPORT_GAP))}px`,
        margin: '0',
        maxHeight: `${Math.max(MENU_MIN_HEIGHT, available)}px`,
        overflowY: 'auto',
      };
    },

    /**
     * Under the hood, TipTap's NodePos reperesentation of a table is the `tbody` tag,
     * but attributes get set at the table element level. So in this case we need to always
     * return the table element to make sure we are processing and updating its attributes.
     */
    getTableNodePos() {
      const tBodyNode = findNodePosByElement<HTMLTableElement>(this.editor, this.position, 'table');

      return {
        nodePos: tBodyNode.nodePos,
        element: (
          tBodyNode.element?.tagName.toUpperCase() === 'TBODY'
            ? tBodyNode.element?.closest('table')
            : tBodyNode.element
        ),
      };
    },

    /**
     * Sets width and UoM editable state based on values in the following order:
     * 1. Width in `style` attribute
     * 2. Width in `width` attribute
     * 3. Falls back to `100` width with `%` UoM
     */
    setEditableWidth() {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const width = element.width || element.style.width;

      if (!width) {
        this.width = 100;
        this.tableWidthUoM = '%';
        return;
      }

      if (['%', 'px'].some((uom) => element.style.width.endsWith(uom))) {
        this.width = Number(width.replace(/%|px/, ''));
        this.tableWidthUoM = width.includes('%') ? '%' : 'px';
        return;
      }

      this.width = Number(width);
      this.tableWidthUoM = 'px';
    },

    /**
     * Sets the border editable state based on values in the following order:
     * 1. `border-width` in `style` attribute.
     * 2. `border` attribute
     * 3. Falls back to `1`.
     */
    setEditableBorder() {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const borderWidth = element.style.borderWidth || element.border;

      if (!borderWidth) {
        this.border = 1;
        return;
      }

      this.border = Number(borderWidth.replace(/%|px/, ''));
    },

    /**
     * Sets the cell spacing editable state based on values in the following order:
     * 1. `border-spacing` in `style`.
     * 2. `cellspacing` attribute
     * 3. Falls back to `1`.
     */
    setEditableCellSpacing() {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const cellSpacing = element.style.borderSpacing || element.cellSpacing;

      if (!cellSpacing) {
        this.cellSpacing = 1;
        return;
      }

      this.cellSpacing = Number(cellSpacing.replace(/%|px/, ''));
    },

    /**
     * Sets the cell padding editable state based on values in the following order:
     * 1. `padding` in `style` attribute of the first `<td>` child found in table element.
     * 2. `cellpadding` attribute
     * 3. Falls back to `1`.
     */
    setEditableCellPadding() {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const cell = element.querySelector('td');
      const cellPadding = cell?.style.padding || element.cellPadding;

      if (!cellPadding) {
        this.cellPadding = 1;
        return;
      }

      this.cellPadding = Number(cellPadding.replace(/%|px/, ''));
    },

    /**
     * Set initial alignment state on table.
     * 1. Checks for `float` or `margin` on the table style attributes.
     * 2. If not in style, checks the align attribute.
     */
    setEditableAlignment() {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      if (element.style.margin.includes('auto')) {
        this.align = 'center';
        return;
      }

      this.align = element.style.float ?? '';
    },

    /**
     * Runs all state setting / resetting methods
     */
    setInitialState() {
      this.width = 100;
      this.tableWidthUoM = '%';
      this.border = 1;
      this.cellSpacing = 1;
      this.cellPadding = 1;
      this.align = '';

      this.setEditableWidth();
      this.setEditableBorder();
      this.setEditableCellPadding();
      this.setEditableCellSpacing();
      this.setEditableAlignment();
    },

    /**
     * Changes value of a key in the `TEditable` state object. Used by other methods
     * after manipulating style values and DOM to sync with the editor's attributes.
     * @param key
     * @param content
     */
    handleChange(newAttrs: Partial<Record<keyof TEditableTable, string | null>>) {
      const { nodePos } = this.getTableNodePos();

      if (!nodePos) {
        return;
      }

      const newValue = { ...nodePos.attributes, ...newAttrs };
      setNodePosAttributes(nodePos, newValue);

      // Small hack to trigger an update. Adds space to beginning then undoes it
      this.editor.commands.insertContentAt(0, ' ', { updateSelection: false });
      this.editor.commands.undo();
    },

    /**
     * Handles the change of width, including accounting or width unit of measurement.
     * Syncs formatted width with the passed `value` prop.
     * @param newVal
     */
    handleWidthChange(newVal: string) {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const formattedWidth = `${newVal}${this.tableWidthUoM}`;
      element.style.width = formattedWidth;

      this.handleChange({
        style: element.getAttribute('style') ?? '',
        width: formattedWidth,
      });
    },

    /**
     * Handles the change of width's unit of measurement ('px' or '%')
     * Syncs formatted width with the passed `value` prop.
     * @param newVal
     */
    handleWidthUoMChange(newVal: 'px' | '%') {
      this.tableWidthUoM = newVal;

      // If width is greater than 100px currently, set it to 100%.
      if (newVal === '%' && (this.width ?? 100) > 100) {
        this.handleWidthChange('100');
        return;
      }

      this.handleWidthChange(`${this.width}`);
    },

    /**
     * Handles the change of border width.
     * Syncs formatted border width with the passed `value` prop.
     * @param newVal
     */
    handleBorderChange(newVal: string) {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const formattedWidth = `${(newVal || '0')}px`;
      element.style.borderWidth = formattedWidth;

      if (!element.style.borderStyle) {
        element.style.borderStyle = 'solid';
      }

      this.handleChange({
        style: element.getAttribute('style') ?? '',
        border: newVal || '0',
      });
    },

    /**
     * Handles the change of border spacing / cell spacing.
     * Syncs formatted border spacing with the passed `value` prop.
     * @param newVal
     */
    handleCellSpacingChange(newVal: string) {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      const formattedWidth = `${(newVal || '0')}px`;
      element.style.borderSpacing = formattedWidth;

      this.handleChange({
        style: element.getAttribute('style') ?? '',
        cellspacing: newVal || '0',
      });
    },

    /**
     * Handles the change of cell padding.
     * Syncs formatted border spacing with the passed `value` prop.
     * @param newVal
     */
    handleCellPaddingChange(newVal: string) {
      const { nodePos, element } = this.getTableNodePos();

      if (!element || !nodePos) {
        return;
      }

      const formattedWidth = `${(newVal || '0')}px`;
      const tds = nodePos.querySelectorAll('tableCell');
      const ths = nodePos.querySelectorAll('tableHeader');

      [...tds, ...ths ].forEach((td) => {
        td.element.style.padding = formattedWidth;
        td.attributes.style = td.element.getAttribute('style') ?? '';
      });

      this.handleChange({ cellpadding: newVal || '0' });
    },

    /**
     * Removes the table node found at position (the current table being edited)
     */
    handleDelete() {
      this.editor.chain().focus(this.position).deleteTable().run();

      if (this.onTableRemove) {
        this.onTableRemove();
      }
    },

    /**
     * Handles alignment of the table against other content.
     * Uses margin auto for center alignment or float left / right for other alignments.
     * @param newVal
     */
    handleAlignChange(newVal: string) {
      const { element } = this.getTableNodePos();

      if (!element) {
        return;
      }

      element.style.margin = '';
      element.style.float = '';

      if (newVal === 'center') {
        element.style.margin = '0 auto';
      }

      if (['left', 'right'].includes(newVal)) {
        element.style.float = newVal;
      }

      this.handleChange({
        style: element.getAttribute('style') ?? '',
        align: newVal || null,
      });
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
</style>
